import { useEffect, useState, lazy, Suspense, startTransition } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { Header } from "./components/Header";
import { Hero } from "./components/Hero";
import WatermarkRemoverClient from "./components/WatermarkRemover.client";
import { useAuth } from "./components/AuthProvider";
import { useSubscription } from "./hooks/useSubscription";
import { SEOHead } from "./components/SEOHead";
import { StandardLayout } from "./components/StandardLayout";

// Import SEO-critical components directly for SSR (not lazy loaded)
// These must render during SSR for Google to index the content
import DocumentCleaners from "./components/DocumentCleaners";
import Footer from "./components/Footer";
import Comparison from "./components/Comparison";
import ResourceCards from "./components/ResourceCards";
import KnowledgeCenter from "./components/KnowledgeCenter";
import FeedbackCTA from "./components/FeedbackCTA";
import FAQ from "./components/FAQ";
import BlogModule from "./components/BlogModule";
import GeminiImageBanner from "./components/GeminiImageBanner";
// BlogPost and CategoryPage are lazy loaded but preloaded via ROUTE_CHUNK_MAP
// to prevent hydration mismatch CLS (see entry-client.tsx)

// Lazy load non-SEO-critical components (modals, auth pages, etc.)
const PaywallPage = lazy(() => import("./components/PaywallPage"));
const PaywallModal = lazy(() => import("./components/PaywallModal"));
const SuccessPage = lazy(() => import("./components/SuccessPage"));
const SubscriptionManagement = lazy(
  () => import("./components/SubscriptionManagement")
);
const Imprint = lazy(() => import("./components/Imprint"));
const TermsOfService = lazy(() => import("./components/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./components/PrivacyPolicy"));
const FeatureRequest = lazy(() => import("./components/FeatureRequest"));
const Auth = lazy(() => import("./components/Auth"));
const AuthCallback = lazy(() => import("./components/AuthCallback"));
const ExtensionAuth = lazy(() =>
  import("./components/ExtensionAuth").then((m) => ({
    default: m.ExtensionAuth,
  }))
);
const ExtensionCallback = lazy(() =>
  import("./components/ExtensionCallback").then((m) => ({
    default: m.ExtensionCallback,
  }))
);
const DetectPage = lazy(() => import("./components/DetectPage"));
const DocumentPage = lazy(() => import("./components/DocumentPage"));
const About = lazy(() => import("./components/About"));
const SpecialDealPage = lazy(() => import("./components/SpecialDealPage"));
const DevDocs = lazy(() => import("./pages/DevDocs"));
const ApiPage = lazy(() => import("./pages/api/ApiPage"));
const ApiWritingToolsPage = lazy(() => import("./pages/api/ApiWritingToolsPage"));
const ApiChatbotMiddlewarePage = lazy(() => import("./pages/api/ApiChatbotMiddlewarePage"));
const ApiContentPlatformsPage = lazy(() => import("./pages/api/ApiContentPlatformsPage"));

// CRITICAL CLS FIX: Import pSEO pages directly instead of lazy loading
// renderToString renders Suspense fallbacks, causing massive CLS when lazy chunks load
// Direct imports ensure full page content is in SSR output
import GeminiWatermarkPage from "./pages/GeminiWatermarkPage";
import GeminiImageWatermarkPage from "./pages/GeminiImageWatermarkPage";
import ClaudeWatermarkPage from "./pages/ClaudeWatermarkPage";
import ChatGPTWatermarkPage from "./pages/ChatGPTWatermarkPage";
import StudentWatermarkPage from "./pages/StudentWatermarkPage";
import FreeChatGPTWatermarkRemoverPage from "./pages/FreeChatGPTWatermarkRemoverPage";
import FreeAiTextWatermarkRemoverPage from "./pages/FreeAiTextWatermarkRemoverPage";
import FreeAiWatermarkRemoverPage from "./pages/FreeAiWatermarkRemoverPage";
import FreeGPTWatermarkRemoverPage from "./pages/FreeGPTWatermarkRemoverPage";
import DetectAIWatermarksPage from "./pages/DetectAIWatermarksPage";
// LCP FIX: KnowledgeBase imported directly to avoid lazy-load waterfall (was 8.8s LCP)
import KnowledgeBase from "./pages/KnowledgeBase";
const BlogList = lazy(() => import("./blog/BlogList"));
const BlogPost = lazy(() => import("./blog/BlogPost"));
const CategoryPage = lazy(() => import("./blog/CategoryPage"));
const TagPage = lazy(() => import("./blog/TagPage"));
const KnowledgeArticle = lazy(() => import("./pages/KnowledgeArticle"));
const SearchPage = lazy(() => import("./pages/SearchPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const FeedbackWidget = lazy(() => import("./components/FeedbackWidget"));
const ClientOnly = lazy(() => import("./components/ClientOnly"));

// Loading fallback component - min-h-screen prevents CLS by reserving space for page content
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-500"></div>
  </div>
);

// Home page component
function HomePage() {
  const { user } = useAuth();

  return (
    <>
      {/* Critical path - loads immediately for LCP */}
      <Hero />
      <div className="container mx-auto px-4 pb-12">
        <WatermarkRemoverClient user={user} />

        {/* SEO-critical components - rendered during SSR (no Suspense) */}
        <DocumentCleaners user={user} />

        {/* Internal linking banner - Gemini Image Watermark Remover */}
        <GeminiImageBanner />

        {/* SEO-critical components - rendered during SSR */}
        <Comparison />
        <ResourceCards />
        <BlogModule />
        <KnowledgeCenter />
        <FeedbackCTA />
        <FAQ asSection />
      </div>
    </>
  );
}

function AppContent() {
  const [showPaywall, setShowPaywall] = useState(false);
  const [paywallReady, setPaywallReady] = useState(false);
  // CLS FIX: Defer transition-colors until after hydration to prevent mobile CLS
  // Lighthouse mobile uses 4x CPU slowdown, causing transition to overlap with extended hydration
  const [isHydrated, setIsHydrated] = useState(false);
  const { user, isLoading } = useAuth();
  const { hasActiveSubscription, loading: subscriptionLoading } =
    useSubscription();
  const location = useLocation();

  // Enable transitions only after hydration completes (prevents CLS on mobile)
  useEffect(() => {
    // HYDRATION FIX: Wrap in startTransition to prevent React Error #421
    startTransition(() => {
      setIsHydrated(true);
    });
  }, []);

  // Lazy load paywall modal after 2 seconds - only if user is signed in without subscription
  useEffect(() => {
    // Wait for auth and subscription to finish loading
    if (isLoading || subscriptionLoading) {
      return;
    }

    // Check if user is on special deal page (all locale variations)
    const isOnSpecialDeal =
      location.pathname === "/specialdeal" ||
      location.pathname === "/en/specialdeal" ||
      location.pathname === "/de/sonderangebot" ||
      location.pathname === "/pt-br/oferta-especial" ||
      location.pathname === "/my/tawaran-khas" ||
      location.pathname === "/es/oferta-especial";

    // Only show paywall if user is signed in but has no active subscription AND not on special deal page
    if (user && !hasActiveSubscription() && !isOnSpecialDeal) {
      // Start prefetching prices immediately (uses the 2-second delay productively)
      import('./utils/pricing/prefetchPrices').then(({ prefetchPrices }) => {
        prefetchPrices().catch(() => {
          // Silently ignore - prices will be fetched when PaywallPage loads
        });
      });

      const timer = setTimeout(() => {
        // HYDRATION FIX: Wrap in startTransition to prevent React Error #421
        startTransition(() => {
          setPaywallReady(true);
          setShowPaywall(true);
        });
      }, 2000); // 2 second delay

      return () => clearTimeout(timer);
    } else if (isOnSpecialDeal) {
      // Hide paywall if user navigates to special deal page
      setShowPaywall(false);
    }
  }, [
    user,
    hasActiveSubscription,
    isLoading,
    subscriptionLoading,
    location.pathname,
  ]);

  // REMOVED: Auth loading spinner that blocked initial render
  // Previously waited for isLoading to complete before showing content
  // This caused 400-600ms delay to LCP while auth initialized
  // Now content renders immediately, auth loads in background (lazy loaded)
  // User experience: instant content display, auth state updates when ready

  return (
    <div className={`min-h-screen flex flex-col bg-white dark:bg-neutral-950 overflow-x-hidden ${isHydrated ? 'transition-colors duration-300' : ''}`}>
      <SEOHead />

      {/* Lazy-loaded Paywall Modal - only loads after 2 seconds if user is signed in without subscription */}
      {paywallReady && (
        <Suspense fallback={null}>
          <PaywallModal
            isOpen={showPaywall}
            onClose={() => setShowPaywall(false)}
          />
        </Suspense>
      )}

      <Header />
      <main className="flex-grow w-full mt-10">
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            {/* Home Pages (Full Width + Internal Container) */}
            <Route path="/" element={<HomePage />} />
            <Route path="/en" element={<HomePage />} />
            <Route path="/en/" element={<HomePage />} />
            <Route path="/de" element={<HomePage />} />
            <Route path="/de/" element={<HomePage />} />
            <Route path="/pt-br" element={<HomePage />} />
            <Route path="/pt-br/" element={<HomePage />} />
            <Route path="/my" element={<HomePage />} />
            <Route path="/my/" element={<HomePage />} />
            <Route path="/es" element={<HomePage />} />
            <Route path="/es/" element={<HomePage />} />
            <Route path="/fr" element={<HomePage />} />
            <Route path="/fr/" element={<HomePage />} />
            <Route path="/ja" element={<HomePage />} />
            <Route path="/ja/" element={<HomePage />} />
            <Route path="/id" element={<HomePage />} />
            <Route path="/id/" element={<HomePage />} />

            {/* pSEO Routes - Full Width (No Container) */}
            <Route path="/remove-chatgpt-watermarks" element={<ChatGPTWatermarkPage />} />
            <Route path="/remove-gemini-watermarks" element={<GeminiWatermarkPage />} />
            <Route path="/remove-claude-watermarks" element={<ClaudeWatermarkPage />} />
            <Route path="/ai-watermark-remover-students" element={<StudentWatermarkPage />} />
            
            <Route path="/en/remove-chatgpt-watermarks" element={<ChatGPTWatermarkPage />} />
            <Route path="/en/remove-gemini-watermarks" element={<GeminiWatermarkPage />} />
            <Route path="/en/remove-claude-watermarks" element={<ClaudeWatermarkPage />} />
            <Route path="/en/ai-watermark-remover-students" element={<StudentWatermarkPage />} />

            <Route path="/de/chatgpt-wasserzeichen-entfernen" element={<ChatGPTWatermarkPage />} />
            <Route path="/de/gemini-wasserzeichen-entfernen" element={<GeminiWatermarkPage />} />
            <Route path="/de/claude-wasserzeichen-entfernen" element={<ClaudeWatermarkPage />} />
            <Route path="/de/ki-wasserzeichen-entferner-studenten" element={<StudentWatermarkPage />} />

            <Route path="/pt-br/remover-marca-dagua-chatgpt" element={<ChatGPTWatermarkPage />} />
            <Route path="/pt-br/remover-marca-dagua-gemini" element={<GeminiWatermarkPage />} />
            <Route path="/pt-br/remover-marca-dagua-claude" element={<ClaudeWatermarkPage />} />
            <Route path="/pt-br/removedor-ai-estudantes" element={<StudentWatermarkPage />} />

            <Route path="/my/buang-tanda-air-chatgpt" element={<ChatGPTWatermarkPage />} />
            <Route path="/my/buang-tanda-air-gemini" element={<GeminiWatermarkPage />} />
            <Route path="/my/buang-tanda-air-claude" element={<ClaudeWatermarkPage />} />
            <Route path="/my/penyingkir-tanda-air-ai-pelajar" element={<StudentWatermarkPage />} />

            <Route path="/es/eliminar-marca-de-agua-chatgpt" element={<ChatGPTWatermarkPage />} />
            <Route path="/es/eliminar-marca-de-agua-gemini" element={<GeminiWatermarkPage />} />
            <Route path="/es/eliminar-marca-de-agua-claude" element={<ClaudeWatermarkPage />} />
            <Route path="/es/eliminar-marca-de-agua-texto-estudiantes" element={<StudentWatermarkPage />} />

            <Route path="/fr/supprimer-filigrane-chatgpt" element={<ChatGPTWatermarkPage />} />
            <Route path="/fr/supprimer-filigrane-gemini" element={<GeminiWatermarkPage />} />
            <Route path="/fr/supprimer-filigrane-claude" element={<ClaudeWatermarkPage />} />
            <Route path="/fr/supprimeur-filigrane-ia-etudiants" element={<StudentWatermarkPage />} />

            <Route path="/ja/chatgpt-watermark-remover" element={<ChatGPTWatermarkPage />} />
            <Route path="/ja/gemini-watermark-remover" element={<GeminiWatermarkPage />} />
            <Route path="/ja/claude-watermark-remover" element={<ClaudeWatermarkPage />} />
            <Route path="/ja/ai-watermark-remover-students" element={<StudentWatermarkPage />} />

            <Route path="/id/hapus-watermark-chatgpt" element={<ChatGPTWatermarkPage />} />
            <Route path="/id/hapus-watermark-gemini" element={<GeminiWatermarkPage />} />
            <Route path="/id/hapus-watermark-claude" element={<ClaudeWatermarkPage />} />
            <Route path="/id/penghapus-watermark-ai-mahasiswa" element={<StudentWatermarkPage />} />

            {/* Gemini IMAGE Watermark Removal - All Locales */}
            <Route path="/remove-gemini-image-watermark" element={<GeminiImageWatermarkPage />} />
            <Route path="/en/remove-gemini-image-watermark" element={<GeminiImageWatermarkPage />} />
            <Route path="/de/gemini-bild-wasserzeichen-entfernen" element={<GeminiImageWatermarkPage />} />
            <Route path="/pt-br/remover-marca-dagua-imagem-gemini" element={<GeminiImageWatermarkPage />} />
            <Route path="/my/buang-tanda-air-imej-gemini" element={<GeminiImageWatermarkPage />} />
            <Route path="/es/eliminar-marca-agua-imagen-gemini" element={<GeminiImageWatermarkPage />} />
            <Route path="/fr/supprimer-filigrane-image-gemini" element={<GeminiImageWatermarkPage />} />
            <Route path="/ja/gemini-image-watermark-remover" element={<GeminiImageWatermarkPage />} />
            <Route path="/id/hapus-watermark-gambar-gemini" element={<GeminiImageWatermarkPage />} />

            {/* Free pSEO Routes - Full Width (No Container) */}
            <Route path="/free-chatgpt-watermark-remover" element={<FreeChatGPTWatermarkRemoverPage />} />
            <Route path="/free-ai-text-watermark-remover" element={<FreeAiTextWatermarkRemoverPage />} />
            <Route path="/free-ai-watermark-remover" element={<FreeAiWatermarkRemoverPage />} />
            <Route path="/free-gpt-watermark-remover" element={<FreeGPTWatermarkRemoverPage />} />

            <Route path="/en/free-chatgpt-watermark-remover" element={<FreeChatGPTWatermarkRemoverPage />} />
            <Route path="/en/free-ai-text-watermark-remover" element={<FreeAiTextWatermarkRemoverPage />} />
            <Route path="/en/free-ai-watermark-remover" element={<FreeAiWatermarkRemoverPage />} />
            <Route path="/en/free-gpt-watermark-remover" element={<FreeGPTWatermarkRemoverPage />} />

            <Route path="/de/kostenloser-chatgpt-wasserzeichen-entferner" element={<FreeChatGPTWatermarkRemoverPage />} />
            <Route path="/de/kostenloser-ki-text-wasserzeichen-entferner" element={<FreeAiTextWatermarkRemoverPage />} />
            <Route path="/de/kostenloser-ki-wasserzeichen-entferner" element={<FreeAiWatermarkRemoverPage />} />
            <Route path="/de/kostenloser-gpt-wasserzeichen-entferner" element={<FreeGPTWatermarkRemoverPage />} />

            <Route path="/pt-br/removedor-marca-dagua-chatgpt-gratis" element={<FreeChatGPTWatermarkRemoverPage />} />
            <Route path="/pt-br/removedor-marca-dagua-texto-ia-gratis" element={<FreeAiTextWatermarkRemoverPage />} />
            <Route path="/pt-br/removedor-marca-dagua-ia-gratis" element={<FreeAiWatermarkRemoverPage />} />
            <Route path="/pt-br/removedor-marca-dagua-gpt-gratis" element={<FreeGPTWatermarkRemoverPage />} />

            <Route path="/my/penyingkir-tanda-air-chatgpt-percuma" element={<FreeChatGPTWatermarkRemoverPage />} />
            <Route path="/my/penyingkir-tanda-air-teks-ai-percuma" element={<FreeAiTextWatermarkRemoverPage />} />
            <Route path="/my/penyingkir-tanda-air-ai-percuma" element={<FreeAiWatermarkRemoverPage />} />
            <Route path="/my/penyingkir-tanda-air-gpt-percuma" element={<FreeGPTWatermarkRemoverPage />} />

            <Route path="/es/eliminador-marca-agua-chatgpt-gratis" element={<FreeChatGPTWatermarkRemoverPage />} />
            <Route path="/es/eliminador-marca-agua-texto-ia-gratis" element={<FreeAiTextWatermarkRemoverPage />} />
            <Route path="/es/eliminador-marca-agua-ia-gratis" element={<FreeAiWatermarkRemoverPage />} />
            <Route path="/es/eliminador-marca-agua-gpt-gratis" element={<FreeGPTWatermarkRemoverPage />} />

            <Route path="/fr/supprimeur-filigrane-chatgpt-gratuit" element={<FreeChatGPTWatermarkRemoverPage />} />
            <Route path="/fr/supprimeur-filigrane-texte-ia-gratuit" element={<FreeAiTextWatermarkRemoverPage />} />
            <Route path="/fr/supprimeur-filigrane-ia-gratuit" element={<FreeAiWatermarkRemoverPage />} />
            <Route path="/fr/supprimeur-filigrane-gpt-gratuit" element={<FreeGPTWatermarkRemoverPage />} />

            <Route path="/ja/free-chatgpt-watermark-remover" element={<FreeChatGPTWatermarkRemoverPage />} />
            <Route path="/ja/free-ai-text-watermark-remover" element={<FreeAiTextWatermarkRemoverPage />} />
            <Route path="/ja/free-ai-watermark-remover" element={<FreeAiWatermarkRemoverPage />} />
            <Route path="/ja/free-gpt-watermark-remover" element={<FreeGPTWatermarkRemoverPage />} />

            <Route path="/id/penghapus-watermark-chatgpt-gratis" element={<FreeChatGPTWatermarkRemoverPage />} />
            <Route path="/id/penghapus-watermark-teks-ai-gratis" element={<FreeAiTextWatermarkRemoverPage />} />
            <Route path="/id/penghapus-watermark-ai-gratis" element={<FreeAiWatermarkRemoverPage />} />
            <Route path="/id/penghapus-watermark-gpt-gratis" element={<FreeGPTWatermarkRemoverPage />} />

            {/* Detect AI Watermarks pSEO Routes */}
            <Route path="/detect-ai-watermarks" element={<DetectAIWatermarksPage />} />
            <Route path="/en/detect-ai-watermarks" element={<DetectAIWatermarksPage />} />
            <Route path="/de/ki-wasserzeichen-erkennen" element={<DetectAIWatermarksPage />} />
            <Route path="/pt-br/detectar-marcas-dagua-ia" element={<DetectAIWatermarksPage />} />
            <Route path="/my/kesan-tanda-air-ai" element={<DetectAIWatermarksPage />} />
            <Route path="/es/detectar-marcas-agua-ia" element={<DetectAIWatermarksPage />} />
            <Route path="/fr/detecter-filigranes-ia" element={<DetectAIWatermarksPage />} />
            <Route path="/ja/detect-ai-watermarks" element={<DetectAIWatermarksPage />} />
            <Route path="/id/deteksi-watermark-ai" element={<DetectAIWatermarksPage />} />

            {/* Standard Pages - Wrapped in StandardLayout */}
            <Route element={<StandardLayout />}>
              <Route path="/search" element={<SearchPage />} />
              <Route path="/about" element={<About />} />
              <Route path="/blog" element={<BlogList />} />
              <Route path="/blog/category/:categorySlug" element={<CategoryPage />} />
              <Route path="/blog/tag/:tagSlug" element={<TagPage />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/imprint" element={<Imprint />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/comparison" element={<Comparison />} />
              <Route path="/detect" element={<DetectPage />} />
              <Route path="/document" element={<DocumentPage />} />
              <Route path="/feature-request" element={<FeatureRequest />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/extension-auth" element={<ExtensionAuth />} />
              <Route path="/auth/extension-callback" element={<ExtensionCallback />} />
              <Route path="/subscription" element={<SubscriptionManagement />} />
              <Route path="/pricing" element={<PaywallPage />} />
              <Route path="/success" element={<SuccessPage />} />
              <Route path="/specialdeal" element={<SpecialDealPage />} />
              <Route path="/knowledge" element={<KnowledgeBase />} />
              <Route path="/knowledge/:slug" element={<KnowledgeArticle />} />
              <Route path="/docs" element={<DevDocs />} />
              <Route path="/api" element={<ApiPage />} />
              <Route path="/de/api-schnittstelle" element={<ApiPage />} />
              <Route path="/pt-br/api-interface" element={<ApiPage />} />
              <Route path="/my/api-antara-muka" element={<ApiPage />} />
              <Route path="/es/api-interfaz" element={<ApiPage />} />
              {/* B2B API Pages - English */}
              <Route path="/api/writing-tools" element={<ApiWritingToolsPage />} />
              <Route path="/en/api/writing-tools" element={<ApiWritingToolsPage />} />
              <Route path="/api/chatbot-middleware" element={<ApiChatbotMiddlewarePage />} />
              <Route path="/en/api/chatbot-middleware" element={<ApiChatbotMiddlewarePage />} />
              <Route path="/api/content-platforms" element={<ApiContentPlatformsPage />} />
              <Route path="/en/api/content-platforms" element={<ApiContentPlatformsPage />} />

              {/* B2B API Pages - German */}
              <Route path="/de/api-schnittstelle/schreibwerkzeuge" element={<ApiWritingToolsPage />} />
              <Route path="/de/api-schnittstelle/chatbot-middleware" element={<ApiChatbotMiddlewarePage />} />
              <Route path="/de/api-schnittstelle/content-plattformen" element={<ApiContentPlatformsPage />} />

              {/* B2B API Pages - Portuguese */}
              <Route path="/pt-br/api-interface/ferramentas-escrita" element={<ApiWritingToolsPage />} />
              <Route path="/pt-br/api-interface/middleware-chatbot" element={<ApiChatbotMiddlewarePage />} />
              <Route path="/pt-br/api-interface/plataformas-conteudo" element={<ApiContentPlatformsPage />} />

              {/* B2B API Pages - Malay */}
              <Route path="/my/api-antara-muka/alat-penulisan" element={<ApiWritingToolsPage />} />
              <Route path="/my/api-antara-muka/middleware-chatbot" element={<ApiChatbotMiddlewarePage />} />
              <Route path="/my/api-antara-muka/platform-kandungan" element={<ApiContentPlatformsPage />} />

              {/* B2B API Pages - Spanish */}
              <Route path="/es/api-interfaz/herramientas-escritura" element={<ApiWritingToolsPage />} />
              <Route path="/es/api-interfaz/middleware-chatbot" element={<ApiChatbotMiddlewarePage />} />
              <Route path="/es/api-interfaz/plataformas-contenido" element={<ApiContentPlatformsPage />} />

              <Route path="/en/search" element={<SearchPage />} />
              <Route path="/en/about" element={<About />} />
              <Route path="/en/blog" element={<BlogList />} />
              <Route path="/en/blog/category/:categorySlug" element={<CategoryPage />} />
              <Route path="/en/blog/tag/:tagSlug" element={<TagPage />} />
              <Route path="/en/blog/:slug" element={<BlogPost />} />
              <Route path="/en/imprint" element={<Imprint />} />
              <Route path="/en/terms" element={<TermsOfService />} />
              <Route path="/en/privacy" element={<PrivacyPolicy />} />
              <Route path="/en/faq" element={<FAQ />} />
              <Route path="/en/comparison" element={<Comparison />} />
              <Route path="/en/detect" element={<DetectPage />} />
              <Route path="/en/document" element={<DocumentPage />} />
              <Route path="/en/feature-request" element={<FeatureRequest />} />
              <Route path="/en/auth" element={<Auth />} />
              <Route path="/en/auth/callback" element={<AuthCallback />} />
              <Route path="/en/extension-auth" element={<ExtensionAuth />} />
              <Route path="/en/auth/extension-callback" element={<ExtensionCallback />} />
              <Route path="/en/subscription" element={<SubscriptionManagement />} />
              <Route path="/en/pricing" element={<PaywallPage />} />
              <Route path="/en/success" element={<SuccessPage />} />
              <Route path="/en/specialdeal" element={<SpecialDealPage />} />
              <Route path="/en/knowledge" element={<KnowledgeBase />} />
              <Route path="/en/knowledge/:slug" element={<KnowledgeArticle />} />
              <Route path="/en/docs" element={<DevDocs />} />

              <Route path="/de/suche" element={<SearchPage />} />
              <Route path="/de/ueber-uns" element={<About />} />
              <Route path="/de/blog" element={<BlogList />} />
              <Route path="/de/blog/kategorie/:categorySlug" element={<CategoryPage />} />
              <Route path="/de/blog/tag/:tagSlug" element={<TagPage />} />
              <Route path="/de/blog/:slug" element={<BlogPost />} />
              <Route path="/de/impressum" element={<Imprint />} />
              <Route path="/de/agb" element={<TermsOfService />} />
              <Route path="/de/datenschutz" element={<PrivacyPolicy />} />
              <Route path="/de/faq" element={<FAQ />} />
              <Route path="/de/vergleiche" element={<Comparison />} />
              <Route path="/de/erkennung" element={<DetectPage />} />
              <Route path="/de/dokument" element={<DocumentPage />} />
              <Route path="/de/feature-anfrage" element={<FeatureRequest />} />
              <Route path="/de/auth" element={<Auth />} />
              <Route path="/de/auth/callback" element={<AuthCallback />} />
              <Route path="/de/extension-auth" element={<ExtensionAuth />} />
              <Route path="/de/auth/extension-callback" element={<ExtensionCallback />} />
              <Route path="/de/abo" element={<SubscriptionManagement />} />
              <Route path="/de/preise" element={<PaywallPage />} />
              <Route path="/de/erfolg" element={<SuccessPage />} />
              <Route path="/de/sonderangebot" element={<SpecialDealPage />} />
              <Route path="/de/wissen" element={<KnowledgeBase />} />
              <Route path="/de/wissen/:slug" element={<KnowledgeArticle />} />

              <Route path="/pt-br/pesquisa" element={<SearchPage />} />
              <Route path="/pt-br/sobre" element={<About />} />
              <Route path="/pt-br/blog" element={<BlogList />} />
              <Route path="/pt-br/blog/categoria/:categorySlug" element={<CategoryPage />} />
              <Route path="/pt-br/blog/tag/:tagSlug" element={<TagPage />} />
              <Route path="/pt-br/blog/:slug" element={<BlogPost />} />
              <Route path="/pt-br/impressao" element={<Imprint />} />
              <Route path="/pt-br/termos" element={<TermsOfService />} />
              <Route path="/pt-br/privacidade" element={<PrivacyPolicy />} />
              <Route path="/pt-br/faq" element={<FAQ />} />
              <Route path="/pt-br/comparacao" element={<Comparison />} />
              <Route path="/pt-br/detecao" element={<DetectPage />} />
              <Route path="/pt-br/documento" element={<DocumentPage />} />
              <Route path="/pt-br/solicitar-recurso" element={<FeatureRequest />} />
              <Route path="/pt-br/auth" element={<Auth />} />
              <Route path="/pt-br/auth/callback" element={<AuthCallback />} />
              <Route path="/pt-br/extension-auth" element={<ExtensionAuth />} />
              <Route path="/pt-br/auth/extension-callback" element={<ExtensionCallback />} />
              <Route path="/pt-br/assinatura" element={<SubscriptionManagement />} />
              <Route path="/pt-br/precos" element={<PaywallPage />} />
              <Route path="/pt-br/sucesso" element={<SuccessPage />} />
              <Route path="/pt-br/oferta-especial" element={<SpecialDealPage />} />
              <Route path="/pt-br/conhecimento" element={<KnowledgeBase />} />
              <Route path="/pt-br/conhecimento/:slug" element={<KnowledgeArticle />} />

              <Route path="/my/carian" element={<SearchPage />} />
              <Route path="/my/tentang-kami" element={<About />} />
              <Route path="/my/blog" element={<BlogList />} />
              <Route path="/my/blog/kategori/:categorySlug" element={<CategoryPage />} />
              <Route path="/my/blog/tag/:tagSlug" element={<TagPage />} />
              <Route path="/my/blog/:slug" element={<BlogPost />} />
              <Route path="/my/cetakan" element={<Imprint />} />
              <Route path="/my/terma" element={<TermsOfService />} />
              <Route path="/my/privasi" element={<PrivacyPolicy />} />
              <Route path="/my/faq" element={<FAQ />} />
              <Route path="/my/perbandingan" element={<Comparison />} />
              <Route path="/my/pengesanan" element={<DetectPage />} />
              <Route path="/my/dokumen" element={<DocumentPage />} />
              <Route path="/my/permintaan-ciri" element={<FeatureRequest />} />
              <Route path="/my/auth" element={<Auth />} />
              <Route path="/my/auth/callback" element={<AuthCallback />} />
              <Route path="/my/extension-auth" element={<ExtensionAuth />} />
              <Route path="/my/auth/extension-callback" element={<ExtensionCallback />} />
              <Route path="/my/langganan" element={<SubscriptionManagement />} />
              <Route path="/my/harga" element={<PaywallPage />} />
              <Route path="/my/kejayaan" element={<SuccessPage />} />
              <Route path="/my/tawaran-khas" element={<SpecialDealPage />} />
              <Route path="/my/pengetahuan" element={<KnowledgeBase />} />
              <Route path="/my/pengetahuan/:slug" element={<KnowledgeArticle />} />

              <Route path="/es/buscar" element={<SearchPage />} />
              <Route path="/es/acerca-de" element={<About />} />
              <Route path="/es/blog" element={<BlogList />} />
              <Route path="/es/blog/categoria/:categorySlug" element={<CategoryPage />} />
              <Route path="/es/blog/etiqueta/:tagSlug" element={<TagPage />} />
              <Route path="/es/blog/:slug" element={<BlogPost />} />
              <Route path="/es/conocimiento" element={<KnowledgeBase />} />
              <Route path="/es/conocimiento/:slug" element={<KnowledgeArticle />} />
              <Route path="/es/aviso-legal" element={<Imprint />} />
              <Route path="/es/terminos" element={<TermsOfService />} />
              <Route path="/es/privacidad" element={<PrivacyPolicy />} />
              <Route path="/es/faq" element={<FAQ />} />
              <Route path="/es/comparacion" element={<Comparison />} />
              <Route path="/es/detectar" element={<DetectPage />} />
              <Route path="/es/documento" element={<DocumentPage />} />
              <Route path="/es/solicitar-funcion" element={<FeatureRequest />} />
              <Route path="/es/auth" element={<Auth />} />
              <Route path="/es/auth/callback" element={<AuthCallback />} />
              <Route path="/es/extension-auth" element={<ExtensionAuth />} />
              <Route path="/es/auth/extension-callback" element={<ExtensionCallback />} />
              <Route path="/es/suscripcion" element={<SubscriptionManagement />} />
              <Route path="/es/precios" element={<PaywallPage />} />
              <Route path="/es/exito" element={<SuccessPage />} />
              <Route path="/es/oferta-especial" element={<SpecialDealPage />} />

              {/* French Routes */}
              <Route path="/fr/recherche" element={<SearchPage />} />
              <Route path="/fr/a-propos" element={<About />} />
              <Route path="/fr/blog" element={<BlogList />} />
              <Route path="/fr/blog/categorie/:categorySlug" element={<CategoryPage />} />
              <Route path="/fr/blog/tag/:tagSlug" element={<TagPage />} />
              <Route path="/fr/blog/:slug" element={<BlogPost />} />
              <Route path="/fr/connaissances" element={<KnowledgeBase />} />
              <Route path="/fr/connaissances/:slug" element={<KnowledgeArticle />} />
              <Route path="/fr/mentions-legales" element={<Imprint />} />
              <Route path="/fr/conditions" element={<TermsOfService />} />
              <Route path="/fr/confidentialite" element={<PrivacyPolicy />} />
              <Route path="/fr/faq" element={<FAQ />} />
              <Route path="/fr/comparaison" element={<Comparison />} />
              <Route path="/fr/detection" element={<DetectPage />} />
              <Route path="/fr/document" element={<DocumentPage />} />
              <Route path="/fr/demande-fonctionnalite" element={<FeatureRequest />} />
              <Route path="/fr/auth" element={<Auth />} />
              <Route path="/fr/auth/callback" element={<AuthCallback />} />
              <Route path="/fr/extension-auth" element={<ExtensionAuth />} />
              <Route path="/fr/auth/extension-callback" element={<ExtensionCallback />} />
              <Route path="/fr/abonnement" element={<SubscriptionManagement />} />
              <Route path="/fr/tarifs" element={<PaywallPage />} />
              <Route path="/fr/succes" element={<SuccessPage />} />
              <Route path="/fr/offre-speciale" element={<SpecialDealPage />} />
              <Route path="/fr/api" element={<ApiPage />} />
              <Route path="/fr/api/outils-ecriture" element={<ApiWritingToolsPage />} />
              <Route path="/fr/api/middleware-chatbot" element={<ApiChatbotMiddlewarePage />} />
              <Route path="/fr/api/plateformes-contenu" element={<ApiContentPlatformsPage />} />

              {/* Japanese Routes */}
              <Route path="/ja/search" element={<SearchPage />} />
              <Route path="/ja/about" element={<About />} />
              <Route path="/ja/blog" element={<BlogList />} />
              <Route path="/ja/blog/category/:categorySlug" element={<CategoryPage />} />
              <Route path="/ja/blog/tag/:tagSlug" element={<TagPage />} />
              <Route path="/ja/blog/:slug" element={<BlogPost />} />
              <Route path="/ja/knowledge" element={<KnowledgeBase />} />
              <Route path="/ja/knowledge/:slug" element={<KnowledgeArticle />} />
              <Route path="/ja/imprint" element={<Imprint />} />
              <Route path="/ja/terms" element={<TermsOfService />} />
              <Route path="/ja/privacy" element={<PrivacyPolicy />} />
              <Route path="/ja/faq" element={<FAQ />} />
              <Route path="/ja/comparison" element={<Comparison />} />
              <Route path="/ja/detect" element={<DetectPage />} />
              <Route path="/ja/document" element={<DocumentPage />} />
              <Route path="/ja/feature-request" element={<FeatureRequest />} />
              <Route path="/ja/auth" element={<Auth />} />
              <Route path="/ja/auth/callback" element={<AuthCallback />} />
              <Route path="/ja/extension-auth" element={<ExtensionAuth />} />
              <Route path="/ja/auth/extension-callback" element={<ExtensionCallback />} />
              <Route path="/ja/subscription" element={<SubscriptionManagement />} />
              <Route path="/ja/pricing" element={<PaywallPage />} />
              <Route path="/ja/success" element={<SuccessPage />} />
              <Route path="/ja/specialdeal" element={<SpecialDealPage />} />
              <Route path="/ja/api" element={<ApiPage />} />
              <Route path="/ja/api/writing-tools" element={<ApiWritingToolsPage />} />
              <Route path="/ja/api/chatbot-middleware" element={<ApiChatbotMiddlewarePage />} />
              <Route path="/ja/api/content-platforms" element={<ApiContentPlatformsPage />} />

              {/* Indonesian Routes */}
              <Route path="/id/cari" element={<SearchPage />} />
              <Route path="/id/tentang" element={<About />} />
              <Route path="/id/blog" element={<BlogList />} />
              <Route path="/id/blog/kategori/:categorySlug" element={<CategoryPage />} />
              <Route path="/id/blog/tag/:tagSlug" element={<TagPage />} />
              <Route path="/id/blog/:slug" element={<BlogPost />} />
              <Route path="/id/pengetahuan" element={<KnowledgeBase />} />
              <Route path="/id/pengetahuan/:slug" element={<KnowledgeArticle />} />
              <Route path="/id/cetak" element={<Imprint />} />
              <Route path="/id/syarat" element={<TermsOfService />} />
              <Route path="/id/privasi" element={<PrivacyPolicy />} />
              <Route path="/id/faq" element={<FAQ />} />
              <Route path="/id/perbandingan" element={<Comparison />} />
              <Route path="/id/deteksi" element={<DetectPage />} />
              <Route path="/id/dokumen" element={<DocumentPage />} />
              <Route path="/id/permintaan-fitur" element={<FeatureRequest />} />
              <Route path="/id/auth" element={<Auth />} />
              <Route path="/id/auth/callback" element={<AuthCallback />} />
              <Route path="/id/extension-auth" element={<ExtensionAuth />} />
              <Route path="/id/auth/extension-callback" element={<ExtensionCallback />} />
              <Route path="/id/langganan" element={<SubscriptionManagement />} />
              <Route path="/id/harga" element={<PaywallPage />} />
              <Route path="/id/sukses" element={<SuccessPage />} />
              <Route path="/id/penawaran-khusus" element={<SpecialDealPage />} />
              <Route path="/id/api" element={<ApiPage />} />
              <Route path="/id/api/alat-penulisan" element={<ApiWritingToolsPage />} />
              <Route path="/id/api/middleware-chatbot" element={<ApiChatbotMiddlewarePage />} />
              <Route path="/id/api/platform-konten" element={<ApiContentPlatformsPage />} />

              {/* 404 Catch-all - MUST be last route */}
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </Suspense>
      </main>
      {/* Footer - below the fold, lazy loaded */}
      <Suspense fallback={null}>
        <Footer />
      </Suspense>

      {/* Feedback Widget - client-only, lazy loaded */}
      <Suspense fallback={null}>
        <ClientOnly>
          <FeedbackWidget />
        </ClientOnly>
      </Suspense>
    </div>
  );
}

function App() {
  return <AppContent />;
}

export default App;
